'use client'
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useState } from 'react'

import { Heading } from '@/components/primitives/Heading/Heading'
import { cn } from '@/lib/utils'

import type { CarouselSectionType } from '@/types/contentful/carouselSectionType'
import type { IconProps } from '@phosphor-icons/react'
import type { UseEmblaCarouselType } from 'embla-carousel-react'

type ThemeProps = {
  [key in 'default' | 'primary' | 'light' | 'dark' | 'white']: {
    backgroundColour: string
    titleColour: 'primary' | 'default' | 'white'
    container: string
    button: {
      active: string
      disabled: string
      hover?: string
    }
  }
}

const THEME: ThemeProps = {
  default: {
    backgroundColour: 'bg-grey-100',
    titleColour: 'default',
    container: 'gap-[1px] bg-grey-200',
    button: {
      active: 'bg-primary-500',
      disabled: 'border-primary-200 border opacity-40',
      hover: 'hover:bg-primary-700',
    },
  },
  primary: {
    backgroundColour: 'bg-grey-100',
    titleColour: 'primary',
    container: 'gap-4 md:gap-8',
    button: {
      active: 'bg-primary-500',
      disabled: 'border-primary-200 border opacity-40',
      hover: 'hover:bg-primary-700',
    },
  },
  light: {
    backgroundColour: 'bg-primary-100',
    titleColour: 'primary',
    container: 'gap-4 md:gap-8',
    button: {
      active: 'bg-primary-500',
      disabled: 'border-primary-200 border opacity-40',
      hover: 'hover:bg-primary-700',
    },
  },
  dark: {
    backgroundColour: 'bg-primary-500',
    titleColour: 'white',
    container: 'gap-4 md:gap-8',
    button: {
      active: 'bg-primary-300',
      disabled: 'border-primary-200 border opacity-40',
      hover: 'hover:bg-primary-700',
    },
  },
  white: {
    backgroundColour: 'bg-white',
    titleColour: 'primary',
    container: 'gap-4 md:gap-8',
    button: {
      active: 'bg-primary-500',
      disabled: 'border-primary-200 border opacity-40',
      hover: 'hover:bg-primary-700',
    },
  },
}

type CarouselLayoutProps = {
  title?: string
  children: React.ReactNode
  btnVariant?: 'light' | 'dark'
  titleColour?: 'primary' | 'default' | 'white'
  paddingRight?: boolean
  theme: 'default' | 'primary' | 'light' | 'dark' | 'white'
  contained: boolean
  type?: CarouselSectionType['type']
  classNames?: { container?: string }
  /** @deprecated Carousels scroll one item at a time. No longer wired from the CMS; defaults to 1. */
  scrollStep?: number
}

export const CarouselLayout = ({ title, children, theme, contained, type, classNames, scrollStep = 1 }: CarouselLayoutProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start', containScroll: 'trimSnaps', dragFree: false })
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const { backgroundColour, titleColour, container } = THEME[theme]

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollTo(emblaApi.selectedScrollSnap() - scrollStep)
  }, [emblaApi, scrollStep])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollTo(emblaApi.selectedScrollSnap() + scrollStep)
  }, [emblaApi, scrollStep])

  const onSelect = useCallback((emblaApi: UseEmblaCarouselType[1]) => {
    if (!emblaApi) return
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <section
      data-name="carousel-section"
      className={cn('w-full overflow-hidden py-8 pr-0 md:py-16 lg:px-0 lg:py-24 desktop:px-30', backgroundColour, contained && 'lg:pr-28', {
        'md:pr-12 lg:pr-12': type === 'Products Carousel',
        'px-0 lg:pr-0 desktop:px-0 md:pl-0': type === 'Recommendations Carousel',
      })}
    >
      <div
        className={cn('mb-6 flex flex-col items-center justify-between gap-6 px-4 md:mb-7 md:flex-row', {
          'md:px-12 desktop:mx-auto desktop:max-w-content desktop:px-0': type !== 'Products Carousel' && type !== 'Recommendations Carousel',
          'md:pr-4 lg:pl-12 xl:max-w-content desktop:mx-auto desktop:max-w-content desktop:px-0': type === 'Products Carousel',
          'w-full lg:pl-0 xl:max-w-full desktop:max-w-full sm:px-0 md:pr-0 flex-row': type === 'Recommendations Carousel',
        })}
      >
        <Heading as="h2" size={theme === 'default' ? 'h4' : 'h1'} colour={titleColour}>
          {title}
        </Heading>
        <div
          className={cn('flex gap-3 md:gap-x-4', {
            'md:mr-0': !contained,
          })}
        >
          <ArrowButton disabled={prevBtnDisabled} onClick={onPrevButtonClick} Icon={CaretLeftIcon} theme={theme} THEME={THEME} />
          <ArrowButton disabled={nextBtnDisabled} onClick={onNextButtonClick} Icon={CaretRightIcon} theme={theme} THEME={THEME} />
        </div>
      </div>

      <div
        className={cn('overflow-hidden pl-4 md:pl-12 desktop:mx-auto desktop:max-w-content desktop:pl-0', {
          'rounded-xl': theme === 'default' && contained,
          'rounded-l-xl md:rounded-xl': theme === 'default' && !contained,
          'pr-4 md:pr-12 desktop:pr-0': type !== 'Products Carousel' && type !== 'Recommendations Carousel',
          'xl:max-w-full desktop:max-w-full md:pl-0 sm:rounded-xl ml-4 sm:ml-0 pl-0': type === 'Recommendations Carousel',
        })}
        ref={emblaRef}
      >
        <div className={cn('flex w-full', container, classNames?.container)}>{children}</div>
      </div>
    </section>
  )
}

type ArrowButtonProps = {
  disabled: boolean
  onClick: () => void
  Icon: React.ForwardRefExoticComponent<Omit<IconProps, 'ref'> & React.RefAttributes<SVGSVGElement>>
  THEME: ThemeProps
  theme: 'default' | 'primary' | 'light' | 'dark' | 'white'
}

const ArrowButton = ({ disabled, onClick, Icon, theme, THEME }: ArrowButtonProps) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-10 items-center justify-center rounded-full md:size-12',
        !disabled && THEME[theme].button?.hover,
        disabled ? THEME[theme].button.disabled : THEME[theme].button.active,
      )}
      title={disabled ? 'Previous' : 'Next'}
    >
      <Icon size={24} className={cn(disabled ? 'text-primary-200' : 'text-white')} />
    </button>
  )
}
